import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { TenantLayout, useTenant } from "@/components/tenant/tenant-layout";
import { completeObligation, listObligations, type ObligationRow } from "@/lib/api/obligations";
import { listFunds } from "@/lib/api/funds";
import { listLps } from "@/lib/api/lps";
import { useRole } from "@/lib/auth/use-role";
import { useAuth } from "@/lib/auth/auth-provider";

type DueBucket = "overdue" | "soon" | "later" | "unscheduled";

function dueBucket(o: ObligationRow): DueBucket {
  if (!o.next_due_date) return "unscheduled";
  const today = new Date().toISOString().slice(0, 10);
  if (o.next_due_date < today) return "overdue";
  const soon = new Date();
  soon.setDate(soon.getDate() + 30);
  if (o.next_due_date <= soon.toISOString().slice(0, 10)) return "soon";
  return "later";
}

const BUCKET_META: Record<DueBucket, { title: string; style: string }> = {
  overdue: { title: "Overdue", style: "text-red-300" },
  soon: { title: "Due in the next 30 days", style: "text-amber-300" },
  later: { title: "Scheduled", style: "text-neutral-300" },
  unscheduled: { title: "Event-driven / unscheduled", style: "text-neutral-500" },
};

function ObligationsInner() {
  const tenant = useTenant();
  const { user } = useAuth();
  const qc = useQueryClient();
  const { canWrite, role } = useRole(tenant.id);
  const canComplete = canWrite || role === "reviewer";
  const [completingId, setCompletingId] = useState<string | null>(null);
  const [notes, setNotes] = useState("");
  const [showAll, setShowAll] = useState(false);

  const { data: funds } = useQuery({
    queryKey: ["funds", tenant.id],
    queryFn: () => listFunds(tenant.id),
  });
  const { data: lps } = useQuery({
    queryKey: ["lps", tenant.id],
    queryFn: () => listLps(tenant.id),
  });
  const {
    data: obligations,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["obligations", tenant.id, showAll],
    queryFn: () => listObligations(tenant.id, { activeOnly: !showAll }),
  });

  const complete = useMutation({
    mutationFn: (o: ObligationRow) => {
      if (!user) throw new Error("Not signed in.");
      return completeObligation(o, user.id, notes);
    },
    onSuccess: () => {
      setCompletingId(null);
      setNotes("");
      void qc.invalidateQueries({ queryKey: ["obligations", tenant.id] });
    },
  });

  const lpName = (id: string): string => lps?.find((l) => l.id === id)?.legal_name ?? "—";
  const fundName = (id: string): string => funds?.find((f) => f.id === id)?.name ?? "—";

  const buckets: Record<DueBucket, ObligationRow[]> = {
    overdue: [],
    soon: [],
    later: [],
    unscheduled: [],
  };
  for (const o of obligations ?? []) buckets[dueBucket(o)].push(o);

  return (
    <>
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Obligations</h1>
          <p className="text-sm text-neutral-400">
            Every dated duty your side letters create — derived at extraction, tracked to
            completion, audit-stamped.
          </p>
        </div>
        <label className="flex items-center gap-2 text-xs text-neutral-400">
          <input
            type="checkbox"
            checked={showAll}
            onChange={(e) => setShowAll(e.target.checked)}
            className="accent-neutral-300"
          />
          Include completed / paused
        </label>
      </header>

      {complete.error ? (
        <div className="rounded border border-red-900/60 bg-red-950/30 px-3 py-2 text-xs text-red-300">
          {complete.error.message}
        </div>
      ) : null}

      {isLoading ? (
        <p className="text-sm text-neutral-500">Loading obligations…</p>
      ) : error ? (
        <div className="rounded border border-red-900/60 bg-red-950/30 px-3 py-2 text-xs text-red-300">
          {error.message}
        </div>
      ) : !obligations || obligations.length === 0 ? (
        <div className="rounded border border-neutral-800 bg-neutral-900 p-6 text-center text-sm text-neutral-400">
          <p className="font-medium text-neutral-100">No obligations on the calendar</p>
          <p className="mt-1">
            Obligations appear automatically when extraction finds reporting duties (ESG reports,
            custom statements, tax letters) in your side letters.
          </p>
        </div>
      ) : (
        (["overdue", "soon", "later", "unscheduled"] as const).map((bucket) =>
          buckets[bucket].length === 0 ? null : (
            <section key={bucket} className="space-y-2">
              <h2 className={`text-sm font-semibold ${BUCKET_META[bucket].style}`}>
                {BUCKET_META[bucket].title}{" "}
                <span className="font-normal text-neutral-600">({buckets[bucket].length})</span>
              </h2>
              <div className="overflow-hidden rounded border border-neutral-800">
                <table className="w-full text-left text-sm">
                  <tbody className="divide-y divide-neutral-900">
                    {buckets[bucket].map((o) => (
                      <tr key={o.id} className="hover:bg-neutral-900/50">
                        <td className="w-32 px-3 py-2.5 text-neutral-300">
                          {o.next_due_date ?? "—"}
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="text-neutral-100">{o.title}</div>
                          <div className="text-[11px] text-neutral-500">
                            {fundName(o.fund_id)} · {lpName(o.lp_id)}
                            {o.frequency ? ` · ${o.frequency.replaceAll("_", "-")}` : ""}
                            {o.last_completed_date
                              ? ` · last completed ${o.last_completed_date}`
                              : ""}
                            {o.status !== "active" ? ` · ${o.status.replaceAll("_", " ")}` : ""}
                          </div>
                        </td>
                        {canComplete && o.status === "active" ? (
                          <td className="w-64 px-3 py-2.5 text-right">
                            {completingId === o.id ? (
                              <div className="flex items-center justify-end gap-2">
                                <input
                                  type="text"
                                  value={notes}
                                  onChange={(e) => setNotes(e.target.value)}
                                  placeholder="Completion note…"
                                  className="w-40 rounded border border-neutral-700 bg-neutral-950 px-2 py-1 text-xs text-neutral-200 placeholder:text-neutral-600"
                                />
                                <button
                                  type="button"
                                  disabled={complete.isPending}
                                  onClick={() => complete.mutate(o)}
                                  className="rounded bg-emerald-700 px-2 py-1 text-[11px] font-medium text-emerald-50 hover:bg-emerald-600 disabled:opacity-50"
                                >
                                  Done
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setCompletingId(null)}
                                  className="text-[11px] text-neutral-500 hover:text-neutral-300"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => {
                                  setCompletingId(o.id);
                                  setNotes("");
                                }}
                                className="rounded border border-neutral-700 px-2 py-1 text-[11px] text-neutral-300 hover:bg-neutral-800"
                              >
                                Mark complete
                              </button>
                            )}
                          </td>
                        ) : (
                          <td className="w-64" />
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ),
        )
      )}
    </>
  );
}

export function ObligationsPage() {
  return (
    <TenantLayout>
      <ObligationsInner />
    </TenantLayout>
  );
}
